import React from 'react';
import { Form, Input, Select, Row, Col } from 'antd';
import { Controller } from 'react-hook-form';
import type { FieldError, FieldErrorsImpl, Merge, UseFormReturn } from 'react-hook-form';
import type { Company } from '../../types/company';
import { trpc } from '../../lib/trpc';

const { Option } = Select;

type CompanyFormValues = Partial<Company>;

type FormFieldError = FieldError | Merge<FieldError, FieldErrorsImpl<CompanyFormValues>> | undefined;

const getErrorMessage = (error: FormFieldError): string | undefined => {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    return error;
  }
  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return undefined;
};

interface CompanyEditFormProps {
  form: UseFormReturn<CompanyFormValues>;
  showAdminFields?: boolean;
}

const CompanyEditForm: React.FC<CompanyEditFormProps> = ({ form }) => {
  const { control, formState: { errors } } = form;

  // 業界リスト取得
  const { data: industries, isLoading: industriesLoading } = trpc.industry.getAll.useQuery();

  const prefectures = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
    '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
    '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
    '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
  ];



  return (
    <Form layout="vertical">
      {/* 基本情報 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={<span><span style={{ color: 'red' }}>* </span>会社名</span>}
            validateStatus={errors.name ? 'error' : ''}
            help={getErrorMessage(errors.name)}
            required
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => <Input {...field} placeholder="株式会社サンプル" />}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="会社名（カナ）"
            validateStatus={errors.nameKana ? 'error' : ''}
            help={getErrorMessage(errors.nameKana)}
            tooltip="任意項目"
          >
            <Controller
              name="nameKana"
              control={control}
              render={({ field }) => <Input {...field} placeholder="カブシキガイシャサンプル" />}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 業界選択 */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="業界"
            validateStatus={errors.industryId ? 'error' : ''}
            help={getErrorMessage(errors.industryId)}
            tooltip="任意項目"
          >
            <Controller
              name="industryId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="業界を選択"
                  loading={industriesLoading}
                  allowClear
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                >
                  {industries?.map((industry) => (
                    <Option key={industry.id} value={industry.id}>
                      {industry.name}
                      {industry.nameEn && ` (${industry.nameEn})`}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </Form.Item>
        </Col>
      </Row>


      {/* 住所情報（全て任意） */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="郵便番号"
            validateStatus={errors.postalCode ? 'error' : ''}
            help={getErrorMessage(errors.postalCode)}
            tooltip="任意項目"
          >
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => <Input {...field} placeholder="100-0001" />}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="都道府県"
            validateStatus={errors.prefecture ? 'error' : ''}
            help={getErrorMessage(errors.prefecture)}
            tooltip="任意項目"
          >
            <Controller
              name="prefecture"
              control={control}
              render={({ field }) => (
                <Select {...field} placeholder="選択してください" allowClear>
                  {prefectures.map(pref => (
                    <Option key={pref} value={pref}>{pref}</Option>
                  ))}
                </Select>
              )}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="市区町村"
            validateStatus={errors.city ? 'error' : ''}
            help={getErrorMessage(errors.city)}
            tooltip="任意項目"
          >
            <Controller
              name="city"
              control={control}
              render={({ field }) => <Input {...field} placeholder="千代田区" />}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            label="住所"
            validateStatus={errors.address ? 'error' : ''}
            help={getErrorMessage(errors.address)}
            tooltip="任意項目"
          >
            <Controller
              name="address"
              control={control}
              render={({ field }) => <Input {...field} placeholder="千代田1-1-1" />}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="建物名"
            validateStatus={errors.building ? 'error' : ''}
            help={getErrorMessage(errors.building)}
            tooltip="任意項目"
          >
            <Controller
              name="building"
              control={control}
              render={({ field }) => <Input {...field} placeholder="サンプル建物5F" />}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 連絡先情報（全て任意） */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="電話番号"
            validateStatus={errors.phone ? 'error' : ''}
            help={getErrorMessage(errors.phone)}
            tooltip="任意項目"
          >
            <Controller
              name="phone"
              control={control}
              render={({ field }) => <Input {...field} placeholder="03-1234-5678" />}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="メールアドレス"
            validateStatus={errors.email ? 'error' : ''}
            help={getErrorMessage(errors.email)}
            tooltip="任意項目"
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => <Input {...field} type="email" placeholder="info@example.com" />}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default CompanyEditForm;

